import React, { useEffect, useState } from 'react';
import API, { matchTeachers } from './api';

const DEFAULT_DURATION_MIN = 45;

/* ---------- Time helpers (24h HH:mm) ---------- */
function normalizeTime(val) {
  if (!val) return '';
  const s = String(val).trim();

  // "3:05 PM" -> "15:05"
  const ampm = s.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2], 10);
    const part = ampm[3].toUpperCase();
    if (part === 'PM' && h < 12) h += 12;
    if (part === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
  }

  // "8:00" ili "08:00[:ss]" -> "HH:mm"
  const m2 = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (m2) {
    const h = Math.min(23, Math.max(0, parseInt(m2[1],10)));
    const mi = Math.min(59, Math.max(0, parseInt(m2[2],10)));
    return `${String(h).padStart(2,'0')}:${String(mi).padStart(2,'0')}`;
  }

  return '';
}
function toMinutes(t) {
  const n = normalizeTime(t);
  if (!n) return 0;
  const [h, m] = n.split(':').map(Number);
  return (h||0) * 60 + (m||0);
}
function minutesToHHMM(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
}
function addMinutesHHMM(from, mins = DEFAULT_DURATION_MIN) {
  from = normalizeTime(from);
  if (!from) return '';
  const [h, m] = from.split(':').map(Number);
  const d = new Date(2000, 0, 1, h || 0, m || 0, 0);
  d.setMinutes(d.getMinutes() + (mins || 0));
  if (d.getDate() !== 1) return '23:59';
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

/* ---------- Availability helpers ---------- */
function getAvailableIntervals(intervals = [], blocked = []) {
  let result = intervals.map(i => ({
    from: normalizeTime(i.from_time),
    to: normalizeTime(i.to_time)
  }));

  for (const exc of blocked) {
    const excFrom = normalizeTime(exc.from_time);
    const excTo = normalizeTime(exc.to_time);
    let newResult = [];
    for (const interval of result) {
      if (toMinutes(excTo) <= toMinutes(interval.from) || toMinutes(excFrom) >= toMinutes(interval.to)) {
        newResult.push(interval);
      } else {
        if (toMinutes(excFrom) > toMinutes(interval.from)) {
          newResult.push({ from: interval.from, to: excFrom });
        }
        if (toMinutes(excTo) < toMinutes(interval.to)) {
          newResult.push({ from: excTo, to: interval.to });
        }
      }
    }
    result = newResult;
  }
  return result.filter(i => toMinutes(i.to) > toMinutes(i.from));
}

/* ---------- ID helpers / normalizeTeacher ---------- */
const getTid = (t) => String(t?.id ?? t?.teacher_id ?? t?.teacher?.id ?? '');
const getTidNum = (tOrId) => {
  if (tOrId == null) return null;
  const n = typeof tOrId === 'object'
    ? (tOrId.id ?? tOrId.teacher_id ?? tOrId.teacher?.id)
    : tOrId;
  const parsed = Number(n);
  return Number.isFinite(parsed) ? parsed : null;
};
const normalizeTeacher = (t) => {
  if (!t) return null;
  const id = t.id ?? t.teacher_id ?? t.teacher?.id;
  return { id, ...t };
};

/* ---------- Coverage helpers (teacher week availability) ---------- */
function coverageHint(freeSegments, from, to) {
  const fM = toMinutes(from), tM = toMinutes(to);
  const seg = freeSegments.find(s => toMinutes(s.from) <= fM && fM < toMinutes(s.to));
  if (seg) {
    const segTo = toMinutes(seg.to);
    if (segTo >= tM) return '';
    return `Teacher free until ${minutesToHHMM(segTo)} (needs until ${normalizeTime(to)})`;
  }
  const next = freeSegments.find(s => toMinutes(s.from) >= fM);
  if (next) return `Next free ${normalizeTime(next.from)}–${normalizeTime(next.to)}`;
  const prev = [...freeSegments].reverse().find(s => toMinutes(s.to) <= fM);
  if (prev) return `Last free ended at ${normalizeTime(prev.to)}`;
  return 'No free time this day';
}

/* ---------- Glavna komponenta ---------- */
function ManageVisits() {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeekStart, setSelectedWeekStart] = useState('');
  const [weekDates, setWeekDates] = useState([]);
  const [slots, setSlots] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalDate, setModalDate] = useState('');
  const [modalTime, setModalTime] = useState('');
  const [teacherOptions, setTeacherOptions] = useState({
    default_teacher: null,
    alternatives: [],
    orsMatrixStatus: 'SKIPPED',
    resolved_location: null,
    programRequired: null,
  });
  const [statusMessage, setStatusMessage] = useState('');
  const [studentDefaultTeacherId, setStudentDefaultTeacherId] = useState(null);
  const [useORS, setUseORS] = useState(true);
  const [serverDateStr, setServerDateStr] = useState('');
  const [studentAvailability, setStudentAvailability] = useState({});
  const [isEvent, setIsEvent] = useState(false);
  const [eventEndTime, setEventEndTime] = useState('');

  // teacher availability cache (kao u SmartScheduler)
  const [teacherFreeCache, setTeacherFreeCache] = useState({});

  /* ---------- Effects init ---------- */
  useEffect(() => {
    API.get('/server-time')
      .then(res => setServerDateStr(res.data.serverTime.slice(0, 10)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (statusMessage) {
      const timeout = setTimeout(() => setStatusMessage(''), 3500);
      return () => clearTimeout(timeout);
    }
  }, [statusMessage]);

  useEffect(() => {
    if (selectedStudent) {
      const selected = students.find(s => String(s.id) === String(selectedStudent));
      setStudentDefaultTeacherId(selected?.default_teacher_id || null);
    } else {
      setStudentDefaultTeacherId(null);
    }
  }, [selectedStudent, students]);

  useEffect(() => {
    API.get('/admin/students?assigned_only=0')
      .then(res => setStudents(res.data || []))
      .catch(() => {});
    API.get('/weeks')
      .then(res => setWeekOptions(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedWeekStart && selectedStudent) {
      API.get(`/week-dates/${selectedWeekStart}`)
        .then(res => setWeekDates(res.data || []))
        .catch(() => setWeekDates([]));

      loadVisits(selectedStudent, selectedWeekStart);

      API.get(`/student-availability/realweek/${selectedStudent}/${selectedWeekStart}`)
        .then(res => {
          const newAvail = {};
          Object.entries(res.data || {}).forEach(([date, obj]) => {
            const intervals = (obj.intervals || []).map(i => ({
              from_time: normalizeTime(i.from_time),
              to_time: normalizeTime(i.to_time)
            }));
            const blocked = (obj.blocked || []).map(b => ({
              from_time: normalizeTime(b.from_time),
              to_time: normalizeTime(b.to_time)
            }));
            newAvail[date] = {
              intervals,
              blocked,
              free: getAvailableIntervals(intervals, blocked)
            };
          });
          setStudentAvailability(newAvail);
        })
        .catch(() => {
          setStudentAvailability({});
        });

      setTeacherFreeCache({});
    } else {
      setWeekDates([]);
      setSlots({});
      setStudentAvailability({});
      setTeacherFreeCache({});
    }
  }, [selectedWeekStart, selectedStudent]);

  const loadVisits = (studentId, weekStart) => {
    if (!studentId || !weekStart) return;
    API.get(`/visits/week/${studentId}/${weekStart}`)
      .then(res => {
        // već vraća strukturisano po datumima
        const data = res.data || {};
        // normalize times & teacher
        const norm = {};
        Object.entries(data).forEach(([date, list]) => {
          norm[date] = (list || []).map(s => {
            const from = normalizeTime(s.from);
            const to = normalizeTime(s.type === 'event' ? (s.event_end_time || s.to) : s.to);
            const tnorm = normalizeTeacher(s.teacher || null);
            return {
              ...s,
              teacher: tnorm,
              from,
              to,
              type: s.type || 'standard',
              event_end_time: s.type === 'event'
                ? normalizeTime(s.event_end_time || s.to)
                : null
            };
          });
        });
        setSlots(norm);
      })
      .catch(() => {
        setSlots({});
      });
  };

  /* ---------- Teacher availability (kao SmartScheduler) ---------- */
  async function ensureTeacherWeekFree(teacherId, force = false) {
    const tid = getTidNum(teacherId);
    if (tid == null || !selectedWeekStart) return;
    if (!force && teacherFreeCache[tid]) return;

    try {
      const res = await API.get(`/availability/week/${selectedWeekStart}?teacher_id=${tid}`);
      const a = res.data?.availability || {};
      const e = res.data?.exceptions || {};
      const daysOff = res.data?.day_offs || [];

      const base = {};
      Object.keys(a).forEach(date => {
        const isDayOff = daysOff.includes(date);
        const free = isDayOff
          ? []
          : getAvailableIntervals(
              (a[date] || []).map(x => ({
                from_time: x.from_time,
                to_time: x.to_time
              })),
              (e[date] || []).map(x => ({
                from_time: x.from_time,
                to_time: x.to_time
              }))
            );
        base[date] = { free };
      });

      const busyRes = await API.get(
        `/visits/teacher-week?teacher_id=${tid}&week_start=${selectedWeekStart}`
      );
      const busyByDate = busyRes.data || {};
      Object.keys(base).forEach(date => {
        const free = base[date].free || [];
        const busy = (busyByDate[date] || []).map(i => ({
          from_time: i.from_time,
          to_time: i.to_time
        }));
        base[date].free = getAvailableIntervals(
          free.map(f => ({ from_time: f.from, to_time: f.to })),
          busy
        );
      });

      setTeacherFreeCache(prev => ({ ...prev, [tid]: base }));
    } catch {
      // meh, fallback je BE validacija
    }
  }

  function getTeacherFree(teacherId, date) {
    const tid = getTidNum(teacherId);
    if (tid == null) return [];
    return teacherFreeCache?.[tid]?.[date]?.free || [];
  }

  function canTeacherSetInterval(date, from, to, teacherId) {
    const free = getTeacherFree(teacherId, date);
    if (!free.length) return true; // ako nemamo podatke, ne blokiramo; BE je autoritet
    const f = toMinutes(from), t = toMinutes(to);
    return free.some(seg => f >= toMinutes(seg.from) && t <= toMinutes(seg.to));
  }

  function teacherCoverageHint(date, teacherId, from, to) {
    return coverageHint(getTeacherFree(teacherId, date), from, to);
  }

  /* ---------- Availability checks & snapping ---------- */
  function isSlotAvailableForStudent(date, from, to = null) {
    const avail = studentAvailability[date] || {};
    const freeSlots = avail.free || [];
    from = normalizeTime(from);
    to = normalizeTime(to) || addMinutesHHMM(from, DEFAULT_DURATION_MIN);
    return freeSlots.some(slot =>
      toMinutes(from) >= toMinutes(slot.from) &&
      toMinutes(to) <= toMinutes(slot.to)
    );
  }

  function snapIntoFree(date, desiredFrom, durationMin) {
    const free = (studentAvailability[date]?.free || []).map(f => ({
      from: normalizeTime(f.from),
      to: normalizeTime(f.to)
    }));
    if (!free.length) return null;

    const dFromM = toMinutes(desiredFrom);

    // 1) ako staje u slot koji pokriva desiredFrom
    for (const s of free) {
      const startM = toMinutes(s.from);
      const endM = toMinutes(s.to);
      if (dFromM >= startM && dFromM + durationMin <= endM) {
        const from = minutesToHHMM(dFromM);
        return { from, to: minutesToHHMM(dFromM + durationMin) };
      }
    }

    // 2) sledeći slot koji može da primi duration
    for (const s of free) {
      const startM = toMinutes(s.from);
      const endM = toMinutes(s.to);
      if (endM - startM >= durationMin) {
        const from = minutesToHHMM(startM);
        return { from, to: minutesToHHMM(startM + durationMin) };
      }
    }

    return null;
  }

  function renderStudentIntervals(date) {
    const avail = studentAvailability[date] || { intervals: [], blocked: [], free: [] };
    if (!avail.free || avail.free.length === 0) {
      return (
        <div>
          <span className="text-red-600">
            Student is not available for this day
          </span>
        </div>
      );
    }
    return (
      <div>
        <span className="text-blue-700">Student available time: </span>
        {avail.free.map((f, i) => (
          <span key={i}>
            {f.from}–{f.to}{i < avail.free.length - 1 ? ', ' : ''}
          </span>
        ))}
      </div>
    );
  }

  /* ---------- Slot CRUD ---------- */
  const addSlot = (date) => {
    const freeSlots = studentAvailability[date]?.free || [];
    if (!freeSlots.length) {
      setStatusMessage('Student not available for this date.');
      return;
    }
    const time = freeSlots[0].from;
    const exists = Object.entries(slots).some(
      ([d, slotList]) =>
        d === date &&
        slotList.some(s => normalizeTime(s.from) === normalizeTime(time))
    );
    if (exists) {
      setStatusMessage(`Slot at ${date} ${time} already exists.`);
      return;
    }

    setSlots(prev => ({
      ...prev,
      [date]: [
        ...(prev[date] || []),
        {
          from: time,
          to: addMinutesHHMM(time, DEFAULT_DURATION_MIN),
          teacher: null,
          original_teacher_id: studentDefaultTeacherId,
          reassigned_status: 'none',
          reassigned_response_time: null,
          status: 'draft',
          visit_id: null,
          type: 'standard',
          event_end_time: addMinutesHHMM(time, DEFAULT_DURATION_MIN)
        }
      ]
    }));
  };

  const handleTimeChange = (date, index, field, rawValue) => {
    setSlots(prev => {
      const updated = { ...prev };
      updated[date] = [...(updated[date] || [])];
      updated[date][index] = { ...(updated[date][index] || {}) };
      updated[date][index][field] = rawValue ?? '';
      return updated;
    });
  };

  const handleTimeBlur = (date, index, editedField) => {
    setSlots(prev => {
      const updated = { ...prev };
      const day = [...(updated[date] || [])];
      const slot = { ...(day[index] || {}) };

      const rawFrom = slot.from;
      const rawTo = slot.type === 'event'
        ? (slot.event_end_time || slot.to)
        : slot.to;

      let from = normalizeTime(rawFrom);
      const currentToNorm = normalizeTime(rawTo) || addMinutesHHMM(from, DEFAULT_DURATION_MIN);
      let duration = Math.max(DEFAULT_DURATION_MIN, toMinutes(currentToNorm) - toMinutes(from)) || DEFAULT_DURATION_MIN;

      if (editedField === 'from' && !from) {
        const snap = snapIntoFree(date, '00:00', duration);
        if (snap) {
          from = snap.from;
        } else {
          return prev;
        }
      }

      let to = normalizeTime(rawTo);

      if (editedField === 'from') {
        to = minutesToHHMM(toMinutes(from) + duration);
      } else if (!to) {
        to = addMinutesHHMM(from, duration);
      }

      if (!isSlotAvailableForStudent(date, from, to)) {
        const snap = snapIntoFree(date, from, duration);
        if (snap) {
          from = snap.from;
          to = snap.to;
          setStatusMessage('Time adjusted to nearest available interval.');
        } else {
          return prev;
        }
      }

      if (slot.type === 'event') {
        slot.from = from;
        slot.event_end_time = to;
        slot.to = to;
      } else {
        slot.from = from;
        slot.to = to;
      }

      day[index] = slot;
      updated[date] = day;
      return updated;
    });
  };

  const handleDeleteSlot = async (date, index) => {
    const slot = slots[date][index];
    if (slot.visit_id) {
      const confirmed = window.confirm("This slot is already saved. Do you want to delete it?");
      if (!confirmed) return;
      try {
        await API.delete(`/visits/${slot.visit_id}`);
        setSlots(prev => {
          const updated = { ...prev };
          updated[date].splice(index, 1);
          if (!updated[date].length) delete updated[date];
          return updated;
        });
        setStatusMessage('Slot deleted');
      } catch {
        setStatusMessage('Failed to delete slot');
      }
    } else {
      setSlots(prev => {
        const updated = { ...prev };
        updated[date].splice(index, 1);
        if (!updated[date].length) delete updated[date];
        return updated;
      });
    }
  };

  /* ---------- MATCH / modal helpers ---------- */
  async function loadMatchMap(studentId) {
    try {
      const res = await matchTeachers(studentId);
      const arr = res.data?.matches || [];
      const map = {};
      arr.forEach(r => {
        const tid = String(r.teacher_id ?? r.id);
        if (!tid) return;
        map[tid] = {
          score: Number(r.score ?? 0),
          reasons: Array.isArray(r.reasons) ? r.reasons : []
        };
      });
      return map;
    } catch {
      return {};
    }
  }

  const decorateWithMatch = (teacher, matchMap) => {
    if (!teacher) return null;
    const n = normalizeTeacher(teacher);
    const mm = matchMap[getTid(n)] || null;
    return { ...n, _match: mm };
  };

  /* ---------- Modal open (teacher-suggestions aligned) ---------- */
  const openTeacherModal = async (date, from) => {
    if (!selectedStudent) {
      alert('Select a student first');
      return;
    }

    const slot = (slots[date] || []).find(
      s => normalizeTime(s.from) === normalizeTime(from)
    );
    if (!slot) return;

    const baseFrom = normalizeTime(slot.from);
    const baseTo = normalizeTime(
      slot.type === 'event'
        ? (slot.event_end_time || slot.to)
        : (slot.to || addMinutesHHMM(baseFrom, DEFAULT_DURATION_MIN))
    ) || addMinutesHHMM(baseFrom, DEFAULT_DURATION_MIN);

    setIsEvent(slot.type === 'event');
    setEventEndTime(slot.type === 'event' ? baseTo : '');
    setModalDate(date);
    setModalTime(baseFrom);

    try {
      const [suggRes, matchMap] = await Promise.all([
        API.get(`/teacher-suggestions`, {
          params: {
            student_id: selectedStudent,
            date,
            from: baseFrom,
            to: baseTo,
            use_ors: useORS ? 1 : 0
          }
        }),
        loadMatchMap(selectedStudent)
      ]);

      const raw = suggRes.data || {};

      // prefetch availability za sve koje dobijemo
      const ids = [];
      if (raw.default_teacher) {
        const idn = getTidNum(raw.default_teacher);
        if (idn != null) ids.push(idn);
      }
      (raw.alternatives || []).forEach(t => {
        const idn = getTidNum(t);
        if (idn != null) ids.push(idn);
      });
      await Promise.all(ids.map(id => ensureTeacherWeekFree(id)));

      const defT = decorateWithMatch(raw.default_teacher, matchMap);
      const alts = (raw.alternatives || [])
        .map(t => decorateWithMatch(t, matchMap))
        .sort(
          (a,b) =>
            (b?._match?.score ?? -Infinity) -
            (a?._match?.score ?? -Infinity)
        );

      setTeacherOptions({
        ...raw,
        default_teacher: defT,
        alternatives: alts
      });
      setShowModal(true);
    } catch (err) {
      setStatusMessage(err?.response?.data?.error || 'Failed to load available teachers');
    }
  };

  /* ---------- Event & teacher checks ---------- */
  function canSetEvent(date, from, to) {
    return isSlotAvailableForStudent(date, from, to);
  }

  /* ---------- Select teacher from modal (with availability checks) ---------- */
  const selectTeacher = (teacherObj) => {
    if (!teacherObj) return;

    const tnorm = normalizeTeacher(teacherObj);
    const tidNum = getTidNum(tnorm);

    const from = normalizeTime(modalTime);
    let to;

    if (isEvent) {
      const end = normalizeTime(eventEndTime);
      if (!end) return;
      if (!canSetEvent(modalDate, from, end)) return;
      if (!canTeacherSetInterval(modalDate, from, end, tidNum)) return;
      to = end;
    } else {
      // standard: postojeći slot to ili default 45min
      const slot = (slots[modalDate] || []).find(
        s => normalizeTime(s.from) === normalizeTime(modalTime)
      );
      const baseTo = normalizeTime(slot?.to) || addMinutesHHMM(from, DEFAULT_DURATION_MIN);
      if (!isSlotAvailableForStudent(modalDate, from, baseTo)) return;
      if (!canTeacherSetInterval(modalDate, from, baseTo, tidNum)) return;
      to = baseTo;
    }

    setSlots(prev => {
      const updated = { ...prev };
      updated[modalDate] = (updated[modalDate] || []).map(slot => {
        if (normalizeTime(slot.from) !== from) return slot;

        const isDefault = tidNum === studentDefaultTeacherId;
        return {
          ...slot,
          from,
          to,
          teacher: tnorm,
          original_teacher_id: studentDefaultTeacherId,
          reassigned_status: isDefault ? 'none' : 'pending',
          reassigned_response_time: isDefault ? null : new Date().toISOString(),
          status: 'draft',
          visit_id: null,
          type: isEvent ? 'event' : 'standard',
          event_end_time: isEvent ? to : null
        };
      });
      return updated;
    });

    setShowModal(false);
  };

  /* ---------- Save (bulk) ---------- */
  const handleSave = async () => {
    if (!selectedStudent || !selectedWeekStart) return;

    const payload = [];
    for (const [date, slotList] of Object.entries(slots)) {
      for (const slot of slotList) {
        if (!slot.teacher || !slot.from || !(slot.to || slot.event_end_time)) continue;

        const from = normalizeTime(slot.from);
        const to = normalizeTime(
          slot.type === 'event'
            ? (slot.event_end_time || slot.to)
            : slot.to
        );
        if (!from || !to) continue;

        if (!isSlotAvailableForStudent(date, from, to)) {
          setStatusMessage(`Slot for ${date} ${from}-${to} is outside student availability`);
          return;
        }

        const scheduled_at = `${date} ${from}`;
        const tid = getTidNum(slot.teacher);

        payload.push({
          student_id: selectedStudent,
          teacher_id: tid,
          scheduled_at,
          notes: '',
          visit_id: slot.visit_id || null,
          reassigned_status: slot.reassigned_status || 'none',
          reassigned_response_time: slot.reassigned_response_time || null,
          original_teacher_id:
            tid !== studentDefaultTeacherId
              ? studentDefaultTeacherId
              : tid,
          status: slot.status || 'draft',
          type: slot.type || 'standard',
          event_end_time: to
        });
      }
    }

    if (!payload.length) {
      setStatusMessage('Nothing to save.');
      return;
    }

    try {
      const res = await API.post('/visits/bulk', { visits: payload });
      if (res.data?.success) {
        setStatusMessage('Visits saved');
        loadVisits(selectedStudent, selectedWeekStart);
      } else {
        setStatusMessage('Save failed');
      }
    } catch {
      setStatusMessage('Save failed');
    }
  };

  /* ---------- UI helpers ---------- */
  const getSlotBorder = (slot) => {
    if (!slot.teacher) return 'border-gray-300';
    if (slot.status === 'canceled') return 'border-purple-700';
    if (slot.reassigned_status === 'rejected') return 'border-red-500';
    if (slot.reassigned_status === 'pending') return 'border-yellow-400';
    if (slot.reassigned_status === 'accepted') return 'border-green-500';
    if (getTidNum(slot.teacher) === studentDefaultTeacherId) return 'border-green-500';
    return 'border-yellow-400';
  };

  const shouldShowAddSlot = (date) => {
    const slotsForDay = slots[date] || [];
    return (
      slotsForDay.length === 0 ||
      slotsForDay.every(s =>
        s.reassigned_status === 'rejected' || s.status === 'canceled'
      )
    );
  };

  const isProblem = (slot) =>
    (slot.reassigned_status === 'rejected' || slot.status === 'canceled') &&
    slot.teacher && getTidNum(slot.teacher) === studentDefaultTeacherId;

  const isPast = (date) => date < serverDateStr;

  /* ---------- RENDER ---------- */
  return (
    <div className="p-1 max-w-6xl mx-auto">
      <h2 className="text-lg font-bold mb-4">Manage Weekly Visits</h2>

      <div className="mb-4 flex flex-col gap-4 sm:flex-row">
        <div>
          <label className="block mb-1">Select Student:</label>
          <select
            className="border px-2 py-1 w-auto max-w-xs"
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
          >
            <option value="">-- Choose student --</option>
            {students
              .slice()
              .sort(
                (a, b) =>
                  (a.last_name || '').localeCompare(b.last_name || '') ||
                  (a.first_name || '').localeCompare(b.first_name || '')
              )
              .map(s => (
                <option key={s.id} value={s.id}>
                  {(s.last_name || '') + ', ' + (s.first_name || '')}
                </option>
              ))}
          </select>
        </div>
        {selectedStudent && (
          <div>
            <label className="block mb-1">Select Week:</label>
            <select
              className="border px-2 py-1 w-auto max-w-xs"
              value={selectedWeekStart}
              onChange={(e) => setSelectedWeekStart(e.target.value)}
            >
              <option value="">-- Choose week --</option>
              {weekOptions.map((w, idx) => (
                <option key={idx} value={w.value}>{w.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="flex items-center mb-4">
        <span className="mr-3 text-sm font-medium text-gray-700">
          Calculate travel cost (slower)
        </span>
        <button
          type="button"
          className={
            "relative inline-flex h-6 w-11 items-center rounded-full transition-colors " +
            (useORS ? "bg-blue-600" : "bg-gray-300")
          }
          onClick={() => setUseORS(v => !v)}
        >
          <span
            className={
              "inline-block h-4 w-4 transform rounded-full bg-white transition-transform " +
              (useORS ? "translate-x-6" : "translate-x-1")
            }
          />
        </button>
        <span className="ml-3 text-xs text-gray-400">
          {useORS ? "ON" : "OFF"}
        </span>
      </div>

      {weekDates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {weekDates.map(({ date, dayName }) => (
            <div key={date} className="border p-3 rounded shadow min-h-[180px] flex flex-col">
              <h4
                className={
                  `font-semibold mb-2 px-2 py-1 rounded ` +
                  (isPast(date)
                    ? 'text-gray-400 bg-gray-50'
                    : 'text-gray-800 bg-gray-100')
                }
              >
                {dayName} – {date}
              </h4>

              <div className="mb-2">{renderStudentIntervals(date)}</div>

              {(slots[date] || []).map((slot, index) => {
                const borderColor = getSlotBorder(slot);
                const problem = isProblem(slot);
                const dateIsPast = isPast(date);

                const labelFrom = normalizeTime(slot.from);
                const labelTo = normalizeTime(
                  slot.type === 'event'
                    ? (slot.event_end_time || slot.to)
                    : slot.to
                );

                const slotLabel = slot.type === 'event'
                  ? (
                    <span className="inline-block ml-2 px-2 py-0.5 text-xs rounded bg-yellow-100 text-orange-800">
                      Event ({labelFrom}–{labelTo})
                    </span>
                  )
                  : (
                    <span className="inline-block ml-2 px-2 py-0.5 text-xs rounded bg-green-100 text-green-800">
                      Standard ({labelFrom}–{labelTo})
                    </span>
                  );

                return (
                  <div
                    key={`${date}-${index}`}
                    className={
                      `mb-2 rounded p-2 flex flex-col gap-1 relative border-2 ` +
                      (slot.status === 'complete'
                        ? 'border-gray-400 bg-gray-100 text-gray-500 opacity-80'
                        : borderColor)
                    }
                  >
                    <div className="flex gap-2 items-center">
                      {slot.visit_id ? (
                        <span className="font-mono">
                          {labelFrom} – {labelTo}
                        </span>
                      ) : (
                        <>
                          <input
                            type="time"
                            lang="en-GB"
                            step="60"
                            value={slot.from || ''}
                            onChange={e => handleTimeChange(date, index, 'from', e.target.value)}
                            onBlur={() => handleTimeBlur(date, index, 'from')}
                            className="border px-2 py-1 w-24"
                            disabled={slot.type === 'event'}
                          />
                          <span>to</span>
                          <input
                            type="time"
                            lang="en-GB"
                            step="60"
                            value={
                              slot.type === 'event'
                                ? (slot.event_end_time || slot.to || '')
                                : (slot.to || '')
                            }
                            onChange={e =>
                              handleTimeChange(
                                date,
                                index,
                                slot.type === 'event' ? 'event_end_time' : 'to',
                                e.target.value
                              )
                            }
                            onBlur={() =>
                              handleTimeBlur(
                                date,
                                index,
                                slot.type === 'event' ? 'event_end_time' : 'to'
                              )
                            }
                            className="border px-2 py-1 w-24"
                          />
                        </>
                      )}
                      {slotLabel}
                      {slot.status === 'complete' ? (
                        <span className="ml-2 text-gray-500 italic text-sm">
                          Visit completed
                        </span>
                      ) : (
                        !dateIsPast && (
                          <button
                            className="ml-2 text-red-500"
                            title="Delete slot"
                            onClick={() => handleDeleteSlot(date, index)}
                          >
                            🗑
                          </button>
                        )
                      )}
                    </div>

                    <div className="text-sm flex justify-between items-center">
                      <div>
                        {/* Teacher info */}
                        <div>
                          Teacher:{' '}
                          {slot.teacher ? (
                            <span
                              className={
                                `font-semibold ` +
                                (slot.status === 'canceled'
                                  ? 'text-purple-700'
                                  : slot.reassigned_status === 'rejected'
                                  ? 'text-red-600'
                                  : getTidNum(slot.teacher) === studentDefaultTeacherId
                                  ? 'text-green-700'
                                  : 'text-yellow-700')
                              }
                            >
                              {slot.teacher.name}
                            </span>
                          ) : (
                            <span className="text-red-600">Not assigned</span>
                          )}
                        </div>

                        {/* Reassign info */}
                        {slot.original_teacher_id &&
                          slot.teacher &&
                          getTidNum(slot.teacher) !== slot.original_teacher_id && (
                          <div
                            className={
                              `text-xs ` +
                              (slot.status === 'complete'
                                ? 'text-gray-500 font-semibold'
                                : slot.reassigned_status === 'pending'
                                ? 'text-yellow-600'
                                : slot.reassigned_status === 'accepted'
                                ? 'text-green-700'
                                : slot.reassigned_status === 'rejected'
                                ? 'text-red-600'
                                : 'text-yellow-600')
                            }
                          >
                            {slot.status === 'complete'
                              ? 'Complete'
                              : <>
                                  Substitute for original teacher
                                  {slot.reassigned_status === 'pending' && ' – pending'}
                                  {slot.reassigned_status === 'accepted' && ' – accepted'}
                                  {slot.reassigned_status === 'rejected' && ' – rejected'}
                                </>
                            }
                          </div>
                        )}

                        {/* Canceled / rejected messages */}
                        {slot.status === 'canceled' && (
                          <>
                            <div className="text-xs text-purple-700">
                              Visit canceled – please choose another teacher
                            </div>
                            {slot.notes && (
                              <div className="text-xs text-purple-500 italic mt-1">
                                {slot.notes}
                              </div>
                            )}
                          </>
                        )}
                        {slot.reassigned_status === 'rejected' && (
                          <div className="text-xs text-red-600">
                            Visit rejected – please choose another teacher
                          </div>
                        )}
                      </div>

                      {(problem ||
                        !slot.teacher ||
                        !slot.visit_id ||
                        (slot.original_teacher_id &&
                          ['pending', 'rejected'].includes(slot.reassigned_status)) ||
                        slot.status === 'canceled') && (
                        <button
                          className={
                            `ml-2 px-2 py-1 rounded text-white ` +
                            (slot.status === 'canceled'
                              ? 'bg-purple-700 hover:bg-purple-800'
                              : slot.reassigned_status === 'rejected'
                              ? 'bg-red-600 hover:bg-red-700'
                              : slot.teacher
                              ? 'bg-yellow-500 hover:bg-yellow-600'
                              : 'bg-blue-600 hover:bg-blue-700') +
                            (dateIsPast
                              ? ' opacity-50 cursor-not-allowed pointer-events-none bg-gray-400 hover:bg-gray-400'
                              : '')
                          }
                          onClick={() =>
                            !dateIsPast &&
                            openTeacherModal(date, normalizeTime(slot.from))
                          }
                          disabled={dateIsPast}
                        >
                          Add/Change Teacher
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {shouldShowAddSlot(date) && !isPast(date) && (
                <button
                  className={
                    `mt-2 text-sm px-2 py-1 rounded w-fit ` +
                    ((studentAvailability[date]?.free || []).length
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-gray-400 text-gray-200 cursor-not-allowed')
                  }
                  onClick={() =>
                    (studentAvailability[date]?.free || []).length &&
                    addSlot(date)
                  }
                  disabled={!(studentAvailability[date]?.free || []).length}
                >
                  + Add Slot
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {statusMessage && (
        <div className="mt-4 text-center text-sm font-semibold text-blue-600">
          {statusMessage}
        </div>
      )}

      {selectedWeekStart && (
        <div className="mt-6">
          <button
            onClick={handleSave}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
          >
            Save All Visits
          </button>
        </div>
      )}

      {/* TEACHER MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded max-w-lg w-full relative max-h-[80vh] overflow-y-auto">
            <button
              className="absolute top-2 right-2 text-red-500 text-xl"
              onClick={() => setShowModal(false)}
            >
              ×
            </button>
            <h3 className="text-lg font-bold mb-2">Select Teacher</h3>

            {/* Student location hint */}
            {teacherOptions.resolved_location && (
              <div className="text-xs text-gray-700 mb-2">
                Using student{' '}
                <b>
                  {teacherOptions.resolved_location === 'alt'
                    ? 'ALT address'
                    : 'PRIMARY address'}
                </b>{' '}
                for travel distance calculation.
              </div>
            )}

            {/* ORS status */}
            {teacherOptions.orsMatrixStatus === 'SKIPPED' && (
              <div className="text-blue-700 text-xs mb-2">
                <b>Travel cost calculation skipped</b> for faster loading.
              </div>
            )}
            {teacherOptions.orsMatrixStatus === 'FAILED' && (
              <div className="text-red-700 text-xs mb-2">
                <b>Travel cost calculation failed</b> – showing estimated costs only.
              </div>
            )}
            {teacherOptions.orsMatrixStatus === 'NO_STUDENT_COORD' && (
              <div className="text-yellow-700 text-xs mb-2">
                Cannot calculate travel cost – student has no valid coordinates for this slot.
              </div>
            )}
            {teacherOptions.orsMatrixStatus === 'NO_CANDIDATES' && (
              <div className="text-yellow-700 text-xs mb-2">
                No teachers with known locations for travel matrix – showing hourly+bonus only.
              </div>
            )}

            {/* Event toggle */}
		<label className="flex items-center gap-2 mt-2 mb-2">
		  <input
		    type="checkbox"
		    checked={isEvent}
		    onChange={e => {
		      setIsEvent(e.target.checked);
		      if (!e.target.checked) setEventEndTime('');
		    }}
		  />

		  <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
		    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
		      PI
		    </span>
			<span className="text-xs sm:text-sm">or</span>
		    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">
		      Event visit (multi-hour)
		    </span>
		  </div>
		</label>

            {isEvent && (
              <div className="flex items-center gap-2 mb-2">
                <label>End time:</label>
                <input
                  type="time"
                  lang="en-GB"
                  step="60"
                  value={eventEndTime}
                  min={modalTime}
                  max="23:59"
                  onChange={e => setEventEndTime(e.target.value)}
                  className="border px-2 py-1"
                />
                {eventEndTime &&
                  !canSetEvent(modalDate, modalTime, eventEndTime) && (
                    <span className="text-red-500 text-xs ml-2">
                      Student not available for this interval!
                    </span>
                  )}
              </div>
            )}

            {/* DEFAULT TEACHER */}
            {teacherOptions.default_teacher && (() => {
              const t = teacherOptions.default_teacher;
              const tidNum = getTidNum(t);

              const baseTo = isEvent && eventEndTime
                ? normalizeTime(eventEndTime)
                : (slots[modalDate] || []).find(
                    s => normalizeTime(s.from) === normalizeTime(modalTime)
                  )?.to || addMinutesHHMM(modalTime, DEFAULT_DURATION_MIN);

              const okStudent = !isEvent
                ? isSlotAvailableForStudent(modalDate, modalTime, baseTo)
                : (!eventEndTime || canSetEvent(modalDate, modalTime, eventEndTime));
              const okTeacher = !isEvent
                ? canTeacherSetInterval(modalDate, modalTime, baseTo, tidNum)
                : (!eventEndTime || canTeacherSetInterval(modalDate, modalTime, eventEndTime, tidNum));

              const showWarn = (!okStudent || !okTeacher) && (!isEvent || eventEndTime);

              const teacherHint = (!isEvent && !okTeacher)
                ? teacherCoverageHint(modalDate, tidNum, modalTime, baseTo)
                : (isEvent && eventEndTime && !okTeacher)
                ? teacherCoverageHint(modalDate, tidNum, modalTime, eventEndTime)
                : '';
              const studentHint = (!isEvent && !okStudent)
                ? 'Outside student availability'
                : (isEvent && eventEndTime && !canSetEvent(modalDate, modalTime, eventEndTime))
                ? 'Outside student availability'
                : '';

              return (
                <div className="mb-4 border p-2 rounded bg-green-100">
                  <div className="font-semibold">Default Teacher</div>
                  <div className="text-sm mt-1">
                    <b>Hourly:</b> ${t.hourly_rate?.toFixed?.(2) ?? '—'}<br />
                    <b>Travel:</b>{' '}
                    {typeof t.distance_miles === 'number'
                      ? `${t.distance_miles.toFixed(2)} mi × $${t.travel_cost_per_mile?.toFixed?.(2) ?? '—'}/mi = $${t.travel_cost?.toFixed?.(2) ?? '—'}`
                      : <span className="text-xs text-gray-500">Not calculated</span>}
                    <br />
                    <b>Bonus:</b> ${t.bonus?.toFixed?.(2) ?? '—'}<br />
                    <b>Total: ${t.total_cost?.toFixed?.(2) ?? '—'}</b><br />
                    <span className="text-xs text-gray-500">
                      {t.cost_breakdown}
                    </span>
                  </div>

                  {t._match && (
                    <div className="mt-2">
                      <span className="inline-block px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold">
                        Match score: {t._match.score}
                      </span>
                      {!!t._match.reasons?.length && (
                        <ul className="list-disc ml-5 mt-1 text-xs text-gray-700">
                          {t._match.reasons.slice(0,8).map((r,i)=><li key={i}>{r}</li>)}
                        </ul>
                      )}
                    </div>
                  )}

                  {showWarn && (
                    <div className="text-red-600 text-xs mt-1 space-y-0.5">
                      {teacherHint && <div>{teacherHint}</div>}
                      {studentHint && <div>{studentHint}</div>}
                    </div>
                  )}

                  <button
                    className="mt-2 bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded"
                    onClick={() => {
                      if (isEvent) {
                        if (!eventEndTime || !canSetEvent(modalDate, modalTime, eventEndTime)) return;
                        if (!canTeacherSetInterval(modalDate, modalTime, eventEndTime, tidNum)) return;
                      } else {
                        if (!okStudent || !okTeacher) return;
                      }
                      selectTeacher(t);
                    }}
                    disabled={
                      (isEvent &&
                        (!eventEndTime ||
                          !canSetEvent(modalDate, modalTime, eventEndTime) ||
                          !canTeacherSetInterval(modalDate, modalTime, eventEndTime, tidNum))) ||
                      (!isEvent && (!okStudent || !okTeacher))
                    }
                  >
                    {t.name}
                  </button>
                </div>
              );
            })()}

            {/* ALTERNATIVES */}
            {teacherOptions.alternatives?.length > 0 ? (
              teacherOptions.alternatives.map(t => {
                const tidNum = getTidNum(t);
                const baseTo = isEvent && eventEndTime
                  ? normalizeTime(eventEndTime)
                  : (slots[modalDate] || []).find(
                      s => normalizeTime(s.from) === normalizeTime(modalTime)
                    )?.to || addMinutesHHMM(modalTime, DEFAULT_DURATION_MIN);

                const okStudent = !isEvent
                  ? isSlotAvailableForStudent(modalDate, modalTime, baseTo)
                  : (!eventEndTime || canSetEvent(modalDate, modalTime, eventEndTime));
                const okTeacher = !isEvent
                  ? canTeacherSetInterval(modalDate, modalTime, baseTo, tidNum)
                  : (!eventEndTime || canTeacherSetInterval(modalDate, modalTime, eventEndTime, tidNum));

                const showWarn = (!okStudent || !okTeacher) && (!isEvent || eventEndTime);

                const teacherHint = (!isEvent && !okTeacher)
                  ? teacherCoverageHint(modalDate, tidNum, modalTime, baseTo)
                  : (isEvent && eventEndTime && !okTeacher)
                  ? teacherCoverageHint(modalDate, tidNum, modalTime, eventEndTime)
                  : '';
                const studentHint = (!isEvent && !okStudent)
                  ? 'Outside student availability'
                  : (isEvent && eventEndTime && !canSetEvent(modalDate, modalTime, eventEndTime))
                  ? 'Outside student availability'
                  : '';

                return (
                  <div key={getTid(t)} className="mb-2 border p-2 rounded bg-yellow-100">
                    <div className="font-semibold">{t.name}</div>
                    <div className="text-sm mt-1">
                      <b>Hourly:</b> ${t.hourly_rate?.toFixed?.(2) ?? '—'}<br />
                      <b>Travel:</b>{' '}
                      {t.travel_cost_unknown ? (
                        <span className="text-xs text-yellow-700 font-semibold">
                          Travel cost not calculated – insufficient data or matrix unavailable
                        </span>
                      ) : typeof t.distance_miles === 'number' ? (
                        <span>
                          {t.distance_miles.toFixed(2)} mi × $
                          {t.travel_cost_per_mile?.toFixed?.(2) ?? '—'}/mi = $
                          {t.travel_cost?.toFixed?.(2) ?? '—'}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500">
                          Not calculated
                        </span>
                      )}
                      <br />
                      <b>Bonus:</b> ${t.bonus?.toFixed?.(2) ?? '—'}<br />
                      <b>Total: ${t.total_cost?.toFixed?.(2) ?? '—'}</b><br />
                      <span className="text-xs text-gray-500">
                        {t.cost_breakdown}
                      </span>
                    </div>

                    {t._match && (
                      <div className="mt-2">
                        <span className="inline-block px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold">
                          Match score: {t._match.score}
                        </span>
                        {!!t._match.reasons?.length && (
                          <ul className="list-disc ml-5 mt-1 text-xs text-gray-700">
                            {t._match.reasons.slice(0,8).map((r,i)=><li key={i}>{r}</li>)}
                          </ul>
                        )}
                      </div>
                    )}

                    {showWarn && (
                      <div className="text-red-600 text-xs mt-1 space-y-0.5">
                        {teacherHint && <div>{teacherHint}</div>}
                        {studentHint && <div>{studentHint}</div>}
                      </div>
                    )}

                    <button
                      className="mt-2 bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded"
                      onClick={() => {
                        if (isEvent) {
                          if (!eventEndTime || !canSetEvent(modalDate, modalTime, eventEndTime)) return;
                          if (!canTeacherSetInterval(modalDate, modalTime, eventEndTime, tidNum)) return;
                        } else {
                          if (!okStudent || !okTeacher) return;
                        }
                        selectTeacher(t);
                      }}
                      disabled={
                        (isEvent &&
                          (!eventEndTime ||
                            !canSetEvent(modalDate, modalTime, eventEndTime) ||
                            !canTeacherSetInterval(modalDate, modalTime, eventEndTime, tidNum))) ||
                        (!isEvent && (!okStudent || !okTeacher))
                      }
                    >
                      Select
                    </button>
                  </div>
                );
              })
            ) : (
              <p>No alternative teachers available.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageVisits;











